import { Location, NgIf, NgFor } from '@angular/common';
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../animations/page-transitions';
import { GuaranteeDocumentService } from '../../services/guarantee-document.service';
import { GuaranteeDocument, GuaranteeDocumentFile, GUARANTEE_DOCUMENT_FIELD } from '../../models/guarantee-document';
import { User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { OrderBy, OrderByDirection } from '../../utils/constant';
import { LANGUAGE_CODE_OPTIONS } from '../../models/list';
import { TranslationService } from '../../services/translation.service';
import { SafeUrlPipe } from '../../pipes/safeurl.pipe';
import { LoadingSpinnerComponent } from '../utils/loading-spinner.component';
import { BackButtonComponent } from '../back-button/back-button.component';

@Component({
	selector: 'copo-guarantee-document',
	templateUrl: './guarantee-document.component.html',
	styleUrl: './guarantee-document.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [BackButtonComponent, TranslateModule, LoadingSpinnerComponent, NgIf, NgFor, SafeUrlPipe]
})
export class GuaranteeDocumentComponent implements OnDestroy {
	user: User;
	guaranteeDocuments: GuaranteeDocument[] = [];
	guaranteeDocumentFile: GuaranteeDocumentFile[] = [];
	guaranteeDocumentSub: Subscription;
	isLoading = true;

	readonly languageOptions = LANGUAGE_CODE_OPTIONS;

	constructor(
		private readonly location: Location,
		private readonly guaranteeDocumentService: GuaranteeDocumentService,
		private readonly userService: UserService,
		private readonly translationService: TranslationService
	) {
		this.user = this.userService.user;
		this.guaranteeDocumentSub = this.guaranteeDocumentService
			.getGuaranteeDocument(
				GUARANTEE_DOCUMENT_FIELD,
				undefined,
				0,
				1000,
				new OrderBy('sequence', OrderByDirection.ASC),
				false
			)
			.subscribe({
				next: res => {
					this.guaranteeDocuments = res.results;
				},
				complete: () => {
					this.isLoading = false;
				}
			});
	}

	getDocumentTitle(files: GuaranteeDocumentFile[]) {
		const userFile = files.find(value =>
			value.lang === this.translationService.getLangWithRegion()) ?? files.find(value =>
			value.lang === 'en-GB');
		return userFile?.title ?? '';
	}

	getSortedGuaranteeDocumentList(list: GuaranteeDocument[]) {
		return list.sort((a, b) => this.getDocumentTitle(a.files).localeCompare(this.getDocumentTitle(b.files)));
	}

	getSortedGuaranteeDocumentFileList(list: GuaranteeDocumentFile[]) {
		list.sort((a, b) => this.getLang(a.lang).localeCompare(this.getLang(b.lang)));
		const englishFile = list.find(el => el.lang === 'en-GB');
		if (englishFile) {
			const foundIdx = list.findIndex(el => el.lang === 'en-GB');
			list.splice(foundIdx, 1);
			list.unshift(englishFile);
		}
		return list;
	}

	getURL(file: GuaranteeDocumentFile, id: number) {
		return this.guaranteeDocumentService.getURL(file, id);
	}

	getLang(code: keyof typeof LANGUAGE_CODE_OPTIONS) {
		return this.languageOptions[code];
	}

	getBack() {
		this.location.back();
	}

	ngOnDestroy() {
		this.guaranteeDocumentSub.unsubscribe();
	}
}
